import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import { Button } from '@/components/ui/button';
import {
  Store, Briefcase, Calendar, FolderKanban, Building2, Users, MapPin,
  ArrowLeft, Share2, Loader2, BookOpen, ChevronRight, Trophy, Star, BadgeCheck,
  Crown, Medal, Award, Zap, Heart, Check, Plus, Luggage,
} from 'lucide-react';
import { shareOrCopy } from '../lib/share';
import { setPageMeta, resetPageMeta } from '../lib/seo';
import { toast } from 'sonner';
import VectorMap from '../components/VectorMap';
import { Breadcrumbs, breadcrumbJsonLd } from '../components/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Latvija › [region › municipality › parish (reversed ancestors)] › current.
const buildCrumbs = (overview) => [
  { name: 'Latvija', to: '/karte' },
  ...[...(overview.ancestors || [])].reverse().map((a) => ({ name: a.name, to: `/region/${a.slug}` })),
  { name: overview.name },
];

const CATEGORY_META = {
  shop:     { icon: Store,       label: { lv: 'Veikali', en: 'Shops', ru: 'Магазины' },         hue: '#D97736' },
  service:  { icon: Briefcase,   label: { lv: 'Pakalpojumi', en: 'Services', ru: 'Услуги' },    hue: '#4A6B53' },
  event:    { icon: Calendar,    label: { lv: 'Pasākumi', en: 'Events', ru: 'События' },        hue: '#8B5CF6' },
  project:  { icon: FolderKanban,label: { lv: 'Projekti', en: 'Projects', ru: 'Проекты' },      hue: '#2563EB' },
  business: { icon: Building2,   label: { lv: 'Uzņēmumi', en: 'Businesses', ru: 'Бизнес' },     hue: '#0EA5E9' },
};

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Compact local ecosystem mini-map for the sidebar.
// Pins = ACTIVITY (one per local seller / listing), never user home locations.
function LocalEcosystemMap({ center, sellers, language }) {
  if (!center || !sellers || sellers.length === 0) return null;
  const isLV = language === 'lv';
  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden" data-testid="local-ecosystem-map">
      <div className="p-4 border-b border-[#F3F2EE]">
        <h3 className="font-outfit font-bold text-[#121212] flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4 text-[#D97736]" />
          {(language === 'lv' ? 'Vietējā ekosistēma' : language === 'ru' ? 'Местная экосистема' : 'Local ecosystem')}
        </h3>
        <p className="text-xs text-[#5C5C5C] mt-0.5">
          {isLV
            ? `${sellers.length} ${sellers.length === 1 ? 'pārdevējs' : 'pārdevēji'} kartē`
            : `${sellers.length} ${sellers.length === 1 ? 'seller' : 'sellers'} on the map`}
        </p>
      </div>
      <div className="p-2">
        <VectorMap
          height="256px"
          fitLatvia={false}
          initialCenter={[center.lng, center.lat]}
          initialZoom={12}
          showFilters={false}
          showSearch={false}
          showLocate={false}
          enabledLayers={[]}
          extraMarkers={sellers
            .filter((s) => s.lat != null && s.lng != null)
            .map((s) => ({ lng: s.lng, lat: s.lat, name: s.owner_name, link: `/shop/${s.owner_id}` }))}
        />
      </div>
    </div>
  );
}

function TopSellersLeaderboard({ sellers, regionLabel, language }) {
  if (!sellers || sellers.length === 0) return null;
  const isLV = language === 'lv';

  const RANK_META = [
    { icon: Crown, hue: '#D97736', bg: 'from-[#FFF7ED] to-[#FED7AA]', label: 'TOP 1' },
    { icon: Medal, hue: '#6B7280', bg: 'from-[#F3F4F6] to-[#E5E7EB]', label: 'TOP 2' },
    { icon: Award, hue: '#92400E', bg: 'from-[#FEF3C7] to-[#FDE68A]', label: 'TOP 3' },
  ];

  return (
    <section className="space-y-5" data-testid="region-top-sellers">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D9773620] text-[#D97736]">
          <Trophy className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">
            {language === 'lv' ? `${regionLabel} TOP veikali` : language === 'ru' ? `Топ-продавцы: ${regionLabel}` : `Top sellers in ${regionLabel}`}
          </h2>
          <p className="text-xs text-[#5C5C5C]">
            {(language === 'lv' ? 'Vērtējumu, skatījumu un sludinājumu skaita kopvērtējums' : language === 'ru' ? 'Сводная оценка по отзывам, просмотрам и активным объявлениям' : 'Composite score from reviews, views, and active listings')}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sellers.slice(0, 3).map((s, i) => {
          const meta = RANK_META[i] || RANK_META[2];
          const RankIcon = meta.icon;
          return (
            <Link
              key={s.user_id}
              to={`/shop/${s.user_id}`}
              className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${meta.bg} border border-[#E5E4E0] hover:shadow-lg transition-shadow group`}
              data-testid={`top-seller-${i + 1}`}
            >
              <div
                className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-xs font-bold shadow-sm"
                style={{ color: meta.hue }}
              >
                <RankIcon className="w-3.5 h-3.5" />
                {meta.label}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-white border border-[#E5E4E0] flex items-center justify-center shrink-0">
                    {s.shop_logo_url ? (
                      <img src={s.shop_logo_url} alt={s.shop_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Store className="w-7 h-7 text-[#D97736]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-outfit font-bold text-[#121212] truncate">{s.shop_name}</h3>
                      {s.verified && <BadgeCheck className="w-4 h-4 text-[#4A6B53] shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5C5C] mt-0.5">
                      {s.review_count > 0 ? (
                        <span className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 fill-current text-[#D97736]" />
                          <b>{s.avg_rating}</b>
                          <span>({s.review_count})</span>
                        </span>
                      ) : (
                        <span>{(language === 'lv' ? 'Bez atsauksmēm' : language === 'ru' ? 'Без отзывов' : 'No reviews')}</span>
                      )}
                      <span>·</span>
                      <span>{s.listings_count} {(language === 'lv' ? 'sludinājumi' : language === 'ru' ? 'объявлений' : 'listings')}</span>
                    </div>
                  </div>
                </div>
                {s.featured_listing && (
                  <div className="flex items-center gap-3 bg-white/70 rounded-xl p-2 mt-2">
                    {s.featured_listing.image && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#FAFAFA] shrink-0">
                        <img src={s.featured_listing.image} alt={s.featured_listing.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[#5C5C5C] uppercase tracking-wider">{(language === 'lv' ? 'Iezīmēts' : language === 'ru' ? 'Рекомендуемое' : 'Featured')}</div>
                      <div className="text-sm font-medium text-[#121212] truncate">{s.featured_listing.title}</div>
                    </div>
                  </div>
                )}
                <div className="mt-3 text-xs text-[#5C5C5C] group-hover:text-[#D97736] flex items-center gap-1">
                  {(language === 'lv' ? 'Apskatīt veikalu' : language === 'ru' ? 'Открыть магазин' : 'View shop')}
                  <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// Small dark feed used inside the community panel (Gigs pulse).
function DarkFeed({ title, icon: Icon, accent, items, emptyLV, emptyEN, emptyRU, addTo, language, testid }) {
  const isLV = language === 'lv';
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}26`, color: accent }}>
          <Icon className="w-4 h-4" />
        </span>
        <h3 className="text-white font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>{title}</h3>
        <span className="text-white/40 text-xs">· {items.length}</span>
      </div>
      {items.length === 0 ? (
        <Link to={addTo} className="block rounded-xl border border-dashed border-white/15 p-4 text-center hover:border-white/30 transition-colors group">
          <Plus className="w-4 h-4 mx-auto mb-1 text-white/40 group-hover:text-white/70" />
          <span className="text-white/45 text-xs">{isLV ? emptyLV : language === 'ru' ? (emptyRU || emptyEN) : emptyEN}</span>
        </Link>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 5).map((it) => (
            <li key={it.id}>
              <Link to={it.to} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl hover:bg-white/[0.06] transition-colors group">
                <span className="text-white/85 text-sm truncate group-hover:text-white" style={{ fontFamily: 'Manrope' }}>{it.title}</span>
                {it.meta && <span className="text-xs font-semibold shrink-0" style={{ color: accent }}>{it.meta}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// V2 dark-glass community panel — the SINGLE source of truth for the local
// population/member count. Registered users + avatars + follow action + the
// Gigs (Haltūras) pulse. (Events live only in the main marketplace grid.)
function CommunityPanel({ name, community, following, followerCount, onFollow, followBusy, language }) {
  const isLV = language === 'lv';
  const t = (lv, en, ru) => (isLV ? lv : language === 'ru' ? ru : en);
  if (!community) return null;
  const members = community.members || [];
  const gigs = community.gigs || [];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl border border-white/10 p-7 shadow-2xl"
      style={{ background: '#0A0D0B' }}
      data-testid="community-block"
    >
      <div className="absolute -top-24 -right-16 w-64 h-64 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,159,28,0.20), transparent 70%)' }} />

      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500 mb-4">
          {t('Kopiena', 'Community', 'Сообщество')} · {name}
        </p>
        <div className="flex items-baseline gap-2">
          <span className="text-6xl font-light tracking-tighter text-white"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', textShadow: '0 0 30px rgba(255,159,28,0.35)' }}
            data-testid="community-member-count">
            {community.member_count}
          </span>
          <span className="relative flex h-2.5 w-2.5 mb-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
          </span>
        </div>
        <p className="text-white/60 text-sm mt-1" style={{ fontFamily: 'Manrope' }}>
          {t('reģistrēti iedzīvotāji šeit', 'members registered here', 'жителей зарегистрировано здесь')}
          {followerCount > 0 && ` · ${followerCount} ${t('seko', 'following', 'отслеживают')}`}
        </p>

        {members.length > 0 && (
          <div className="flex items-center mt-5" data-testid="community-avatars">
            <div className="flex -space-x-3">
              {members.slice(0, 8).map((m) => (
                <div key={m.id} title={m.name}
                  className="w-9 h-9 rounded-full border-2 border-[#0A0D0B] overflow-hidden bg-gradient-to-br from-amber-500/40 to-teal-500/30 flex items-center justify-center text-white text-xs font-bold">
                  {m.avatar_url
                    ? <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                    : initials(m.name)}
                </div>
              ))}
            </div>
            {community.member_count > 8 && (
              <span className="ml-3 text-white/50 text-sm">+{community.member_count - 8}</span>
            )}
          </div>
        )}

        <button
          onClick={onFollow}
          disabled={followBusy}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full font-bold text-sm transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60 ${
            following ? 'bg-white/10 text-white border border-white/20' : 'bg-amber-500 text-[#0A0D0B]'
          }`}
          style={following ? {} : { boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
          data-testid="community-follow-btn"
        >
          {followBusy ? <Loader2 className="w-4 h-4 animate-spin" />
            : following ? <><Check className="w-4 h-4" /> {t('Seko', 'Following', 'Отслеживается')}</>
            : <><Heart className="w-4 h-4" /> {t('Sekot apkaimei', 'Follow this area', 'Отслеживать район')}</>}
        </button>

        <div className="mt-7">
          <DarkFeed
            title={t('Haltūras', 'Gigs', 'Халтуры')} icon={Zap} accent="#2EC4B6"
            emptyLV="Vēl nav haltūru šeit — esi pirmais!" emptyEN="No gigs here yet — be the first!" emptyRU="Здесь ещё нет халтур — будь первым!"
            addTo="/gigs" language={language} testid="community-gigs"
            items={gigs.map((g) => ({ id: g.id, to: `/gigs?id=${g.id}`, title: g.title,
              meta: g.budget != null ? `€${Math.round(g.budget)}` : (g.location_name || '') }))}
          />
        </div>
      </div>
    </motion.section>
  );
}

function StatCard({ icon: Icon, value, label, hue, target, testid }) {
  const inner = (
    <div
      className="h-full bg-white border border-[#E5E4E0] rounded-2xl p-5 hover:border-[#D97736] hover:-translate-y-1 transition-transform"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${hue}1A`, color: hue }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-outfit text-3xl font-bold text-[#121212]">{value}</div>
      <div className="text-sm text-[#5C5C5C] mt-1">{label}</div>
    </div>
  );
  if (!target) return inner;
  // Reveal the matching section ON THIS page — works identically for cities,
  // microdistricts and villages (every section is already scoped to the place).
  const go = () => {
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <button type="button" onClick={go} className="block w-full h-full text-left" data-testid={`${testid}-link`}>
      {inner}
    </button>
  );
}

// Category grid with a graceful empty state (never collapses when the place has
// other listings — encourages the first post in an empty category).
function CategorySection({ catKey, items, language, regionSlug, regionLabel }) {
  const meta = CATEGORY_META[catKey];
  const Icon = meta.icon;
  const isEmpty = !items || items.length === 0;
  return (
    <section className="space-y-5" data-testid={`region-section-${catKey}`}>
      <div className="flex items-end justify-between border-b border-black/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${meta.hue}1A`, color: meta.hue }}>
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">{meta.label[language] || meta.label.lv}</h2>
          <span className="text-sm text-[#5C5C5C] hidden sm:inline">· {regionLabel}</span>
        </div>
        {!isEmpty && (
          <Link
            to={`/listings?region=${regionSlug}&category=${catKey}`}
            className="text-sm text-[#D97736] hover:underline flex items-center gap-1 shrink-0"
            data-testid={`region-see-all-${catKey}`}
          >
            {language === 'lv' ? 'Skatīt visus' : language === 'ru' ? 'Все' : 'See all'}
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      {isEmpty ? (
        <Link
          to="/create-listing"
          className="block rounded-2xl border border-dashed border-[#E5E4E0] p-8 text-center hover:border-[#D97736] transition-colors group"
          data-testid={`region-empty-${catKey}`}
        >
          <Plus className="w-5 h-5 mx-auto mb-2 text-[#5C5C5C] group-hover:text-[#D97736]" />
          <span className="text-sm text-[#5C5C5C]">
            {language === 'lv'
              ? `Vēl nav — pievieno pirmo (${(meta.label.lv || '').toLowerCase()})`
              : 'Nothing here yet — add the first'}
          </span>
        </Link>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => <ListingCard key={it.id} listing={it} />)}
        </div>
      )}
    </section>
  );
}

// The place's REAL public events (from db.events — the same ones on the map).
// This is the SINGLE home for events on the page.
function EventsSection({ events, language }) {
  const isLV = language === 'lv';
  if (!events || events.length === 0) return null;
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString((language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-GB'), { day: 'numeric', month: 'short' }); }
    catch { return ''; }
  };
  return (
    <section className="space-y-5" data-testid="region-events">
      <div className="flex items-end justify-between border-b border-black/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#8B5CF61A', color: '#8B5CF6' }}>
            <Calendar className="w-5 h-5" />
          </div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">{(language === 'lv' ? 'Pasākumi' : language === 'ru' ? 'Мероприятия' : 'Events')}</h2>
          <span className="text-sm text-[#5C5C5C] hidden sm:inline">· {events.length}</span>
        </div>
        <Link to="/events" className="text-sm text-[#D97736] hover:underline flex items-center gap-1 shrink-0" data-testid="region-events-all">
          {(language === 'lv' ? 'Visi pasākumi' : language === 'ru' ? 'Все мероприятия' : 'All events')}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {events.map((ev) => (
          <Link
            key={ev.id}
            to={`/events/${ev.id}`}
            className="group rounded-2xl overflow-hidden bg-white border border-[#E5E4E0] hover:shadow-lg transition-shadow"
            data-testid={`region-event-${ev.id}`}
          >
            <div className="h-36 bg-[#F3F2EE] overflow-hidden">
              {ev.image_url ? (
                <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Calendar className="w-8 h-8 text-[#C9C7C0]" /></div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: '#8B5CF6' }}>
                <Calendar className="w-3.5 h-3.5" />{fmt(ev.event_date)}
                <span className="text-[#5C5C5C]">· {ev.is_paid ? (ev.price != null ? `€${ev.price}` : ((language === 'lv' ? 'Maksas' : language === 'ru' ? 'Платно' : 'Paid'))) : ((language === 'lv' ? 'Bez maksas' : language === 'ru' ? 'Бесплатно' : 'Free'))}</span>
              </div>
              <h3 className="font-outfit font-semibold text-[#121212] leading-snug line-clamp-2 group-hover:text-[#D97736]">{ev.title}</h3>
              {ev.location_name && (
                <p className="text-xs text-[#5C5C5C] mt-1 truncate flex items-center gap-1">
                  <MapPin className="w-3 h-3 shrink-0" />{ev.location_name}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Real business PAGES / SHOPS located in this place, as entity cards (the
// "deferred 2b" step). kind='shop' → veikali, kind='business' → uzņēmumi ar lapu.
function BusinessEntitySection({ kind, items, language }) {
  if (!items || items.length === 0) return null;
  const isShop = kind === 'shop';
  const hue = isShop ? '#D97736' : '#0EA5E9';
  const Icon = isShop ? Store : Building2;
  const title = isShop
    ? (language === 'lv' ? 'Veikali' : language === 'ru' ? 'Магазины' : 'Shops')
    : (language === 'lv' ? 'Uzņēmumi' : language === 'ru' ? 'Компании' : 'Businesses');
  return (
    <section className="space-y-5" data-testid={`region-entities-${kind}`}>
      <div className="flex items-end justify-between border-b border-black/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${hue}1A`, color: hue }}>
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">{title}</h2>
          <span className="text-sm text-[#5C5C5C] hidden sm:inline">· {items.length}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((b, i) => (
          <Link
            key={`${b.id}-${b.page_id || 'shop'}-${i}`}
            to={b.link}
            className="group flex items-center gap-4 rounded-2xl bg-white border border-[#E5E4E0] p-4 hover:border-[#D97736] hover:shadow-lg transition-all"
            data-testid={`region-entity-${b.id}`}
          >
            <div className="w-14 h-14 rounded-xl bg-[#F3F2EE] overflow-hidden shrink-0 flex items-center justify-center">
              {b.logo ? (
                <img src={b.logo} alt={b.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <Icon className="w-6 h-6 text-[#C9C7C0]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-outfit font-semibold text-[#121212] leading-snug line-clamp-2 group-hover:text-[#D97736]">{b.name}</h3>
              {b.location_name && (
                <p className="text-xs text-[#5C5C5C] mt-1 truncate flex items-center gap-1">
                  <MapPin className="w-3 h-3 shrink-0" />{b.location_name}
                </p>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-[#C9C7C0] group-hover:text-[#D97736] shrink-0" />
          </Link>
        ))}
      </div>
    </section>
  );
}


// Private classifieds (C2C — Privātie sludinājumi) located in this place.
// Shown as the 6th category alongside shops/services/events/projects/businesses.
function ClassifiedsSection({ items, regionSlug, regionLabel, language }) {
  if (!items || items.length === 0) return null;
  const hue = '#2F7E79';
  const title = language === 'lv' ? 'Sludinājumi' : language === 'ru' ? 'Объявления' : 'Classifieds';
  const priceText = (it) => {
    if (it.price_type === 'free') return language === 'lv' ? 'Bez maksas' : language === 'ru' ? 'Бесплатно' : 'Free';
    if (it.price_type === 'negotiable' && it.price == null) return language === 'lv' ? 'Cena: vienoties' : language === 'ru' ? 'Договорная' : 'Negotiable';
    return it.price != null && it.price !== '' ? `€${it.price}` : '';
  };
  return (
    <section className="space-y-5" data-testid="region-classifieds">
      <div className="flex items-end justify-between border-b border-black/5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${hue}1A`, color: hue }}>
            <Luggage className="w-5 h-5" />
          </div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">{title}</h2>
          <span className="text-sm text-[#5C5C5C] hidden sm:inline">· {regionLabel}</span>
        </div>
        <Link to={`/sludinajumi?region=${regionSlug}`} className="text-sm text-[#2F7E79] hover:underline flex items-center gap-1 shrink-0" data-testid="region-classifieds-all">
          {language === 'lv' ? 'Skatīt visus' : language === 'ru' ? 'Все' : 'See all'}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it) => (
          <Link
            key={it.id}
            to={`/sludinajums/${it.slug || it.id}`}
            className="group flex items-center gap-4 rounded-2xl bg-white border border-[#E5E4E0] p-4 hover:border-[#2F7E79] hover:shadow-lg transition-all"
            data-testid={`region-classified-${it.id}`}
          >
            <div className="w-16 h-16 rounded-xl bg-[#F3F2EE] overflow-hidden shrink-0 flex items-center justify-center">
              {it.image ? (
                <img src={it.image} alt={it.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <Luggage className="w-6 h-6 text-[#C9C7C0]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-outfit font-semibold text-[#121212] leading-snug line-clamp-2 group-hover:text-[#2F7E79]">{it.title}</h3>
              {priceText(it) && <p className="text-sm font-semibold text-[#2F7E79] mt-1">{priceText(it)}</p>}
            </div>
            <ChevronRight className="w-4 h-4 text-[#C9C7C0] group-hover:text-[#2F7E79] shrink-0" />
          </Link>
        ))}
      </div>
    </section>
  );
}


export default function RegionPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [coatOfArms, setCoatOfArms] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [community, setCommunity] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data: overview } = await axios.get(`${API}/regions/${slug}`);
        if (!mounted) return;
        setData(overview);

        const origin = window.location.origin;
        const desc =
          `${overview.name}: ${overview.stats.shops || 0} veikali, ` +
          `${overview.stats.services || 0} pakalpojumi, ` +
          `${overview.stats.events || 0} pasākumi, ` +
          `${overview.stats.projects || 0} projekti.`;
        setPageMeta({
          title: `${overview.name} | KoLV`,
          description: desc,
          image: `${process.env.REACT_APP_BACKEND_URL}/api/og/region/${slug}/banner.png`,
          url: `${origin}/region/${slug}`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Place",
              "name": overview.name,
              "containedInPlace": "Latvia",
              "url": `${origin}/region/${slug}`,
            },
            breadcrumbJsonLd(buildCrumbs(overview), origin),
          ],
        });

        axios.get(`${API}/regions/${slug}/coat-of-arms`)
          .then(({ data }) => mounted && setCoatOfArms(data))
          .catch(() => {});

        axios.get(`${API}/regions/${slug}/top-sellers`)
          .then(({ data }) => mounted && setTopSellers(data?.sellers || []))
          .catch(() => {});

        axios.get(`${API}/regions/${slug}/community`)
          .then(({ data: c }) => {
            if (!mounted) return;
            setCommunity(c);
            setFollowing(!!c.is_following);
            setFollowerCount(c.follower_count || 0);
          })
          .catch(() => {});

        const { data: allCatalogs } = await axios.get(`${API}/catalogs?limit=100`).catch(() => ({ data: [] }));
        if (Array.isArray(allCatalogs) && overview.active_catalogs?.length) {
          const activeIds = new Set(overview.active_catalogs);
          setCatalogs(allCatalogs.filter(c => activeIds.has(c.slug) || activeIds.has(c.id)));
        }
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; resetPageMeta(); };
  }, [slug]);

  const handleShare = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/og/region/${slug}`;
    const res = await shareOrCopy({ url, title: data?.name || slug });
    if (res.ok && res.mode === 'clipboard') {
      toast.success(language === 'lv' ? 'Saite nokopēta' : language === 'ru' ? 'Ссылка скопирована' : 'Link copied');
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.info(language === 'lv' ? 'Pieslēdzies, lai sekotu apkaimei' : language === 'ru' ? 'Войдите, чтобы отслеживать район' : 'Sign in to follow this area');
      navigate('/welcome');
      return;
    }
    setFollowBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/locations/${slug}/follow`);
      setFollowing(res.following);
      setFollowerCount(res.follower_count);
      toast.success(res.following
        ? (language === 'lv' ? `Tu tagad seko ${data?.name}` : language === 'ru' ? `Вы теперь отслеживаете ${data?.name}` : `You now follow ${data?.name}`)
        : (language === 'lv' ? 'Vairs neseko' : language === 'ru' ? 'Больше не отслеживается' : 'Unfollowed'));
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās' : language === 'ru' ? 'Не удалось' : 'Failed');
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D97736]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] px-4">
        <MapPin className="w-16 h-16 text-[#5C5C5C] mb-4" />
        <h1 className="font-outfit text-2xl font-bold text-[#121212] mb-2">
          {language === 'lv' ? 'Pilsēta nav atrasta' : language === 'ru' ? 'Город не найден' : 'City not found'}
        </h1>
        <Button onClick={() => navigate('/')} className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white">
          {language === 'lv' ? 'Atpakaļ uz sākumu' : language === 'ru' ? 'На главную' : 'Back to home'}
        </Button>
      </div>
    );
  }

  const { stats, listings } = data;
  const noListingsAtAll = stats.total_listings === 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="region-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Wayfinding */}
        <button
          onClick={() => {
            const idx = window.history.state?.idx;
            if (typeof idx === 'number' && idx > 0) navigate(-1);
            else navigate(data?.parent?.slug ? `/region/${data.parent.slug}` : '/karte');
          }}
          className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#121212] transition-colors mb-8"
          data-testid="region-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* ============ MAIN COLUMN ============ */}
          <div className="lg:col-span-8 min-w-0 flex flex-col gap-14">
            {/* Hero identity — strictly wayfinding + identity, NO stats subtitle */}
            <header className="flex items-start gap-6 flex-wrap border-b border-black/10 pb-8" data-testid="region-hero">
              <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden p-3 shrink-0">
                {coatOfArms?.url ? (
                  <img
                    src={coatOfArms.url}
                    alt={`${data.name} ģerbonis`}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid="region-coat-of-arms"
                  />
                ) : (
                  <MapPin className="w-12 h-12 text-[#D97736]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-3.5 h-3.5 text-[#5C5C5C] shrink-0" />
                  <Breadcrumbs items={buildCrumbs(data)} />
                </div>
                <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-[#121212] leading-none">
                  {data.name}
                </h1>
                {data.parent && (
                  <p className="text-sm text-[#5C5C5C] mt-3">
                    {language === 'lv'
                      ? `${data.parent.name} reģiona vietējais tirgus`
                      : language === 'ru'
                      ? `Местный рынок региона ${data.parent.name}`
                      : `Local marketplace in ${data.parent.name}`}
                  </p>
                )}
              </div>
              <Button variant="outline" onClick={handleShare} className="border-[#E5E4E0]" data-testid="region-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> {language === 'lv' ? 'Dalīties' : language === 'ru' ? 'Поделиться' : 'Share'}
              </Button>
            </header>

            {/* Ecosystem overview — 5 category counts (users count lives ONLY in the
                community panel, never here, to avoid duplication) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="region-stats">
              <StatCard icon={Store}        value={stats.shops}      label={language === 'lv' ? 'Veikali' : language === 'ru' ? 'Магазины' : 'Shops'}        hue="#D97736" target="region-sec-shop"     testid="stat-shops" />
              <StatCard icon={Briefcase}    value={stats.services}   label={language === 'lv' ? 'Pakalpojumi' : language === 'ru' ? 'Услуги' : 'Services'} hue="#4A6B53" target="region-sec-service"  testid="stat-services" />
              <StatCard icon={Calendar}     value={stats.events}     label={language === 'lv' ? 'Pasākumi' : language === 'ru' ? 'Мероприятия' : 'Events'}      hue="#8B5CF6" target="region-sec-event"    testid="stat-events" />
              <StatCard icon={FolderKanban} value={stats.projects}   label={language === 'lv' ? 'Projekti' : language === 'ru' ? 'Проекты' : 'Projects'}    hue="#2563EB" target="region-sec-project"  testid="stat-projects" />
              <StatCard icon={Building2}    value={stats.businesses} label={language === 'lv' ? 'Uzņēmumi' : language === 'ru' ? 'Бизнес' : 'Businesses'}  hue="#0EA5E9" target="region-sec-business" testid="stat-businesses" />
              <StatCard icon={Luggage}      value={stats.classifieds || 0} label={language === 'lv' ? 'Sludinājumi' : language === 'ru' ? 'Объявления' : 'Classifieds'} hue="#2F7E79" target="region-sec-classified" testid="stat-classifieds" />
            </div>

            <TopSellersLeaderboard sellers={topSellers} regionLabel={data.name} language={language} />

            <div id="region-sec-event" className="scroll-mt-24">
              <EventsSection events={community?.events} language={language} />
            </div>

            <div id="region-sec-classified" className="scroll-mt-24">
              <ClassifiedsSection items={data.region_classifieds} regionSlug={slug} regionLabel={data.name} language={language} />
            </div>

            {noListingsAtAll ? (
              <div className="text-center py-16 bg-white border border-[#E5E4E0] rounded-2xl" data-testid="region-empty-all">
                <MapPin className="w-12 h-12 text-[#5C5C5C] mx-auto mb-4" />
                <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
                  {language === 'lv' ? 'Esi pirmais šajā vietā' : language === 'ru' ? 'Будь первым здесь' : 'Be the first here'}
                </h3>
                <p className="text-sm text-[#5C5C5C] mb-6 max-w-md mx-auto">
                  {language === 'lv'
                    ? `Vēl nav sludinājumu no ${data.name}. Pievieno savu — palīdzi attīstīt vietējo tirgu.`
                    : language === 'ru'
                    ? `Пока нет объявлений из ${data.name}. Добавьте своё — помогите развивать местный рынок.`
                    : `No listings from ${data.name} yet. Add yours — help grow the local marketplace.`}
                </p>
                <Link to="/create-listing">
                  <Button className="bg-[#D97736] hover:bg-[#C06528] text-white">
                    {language === 'lv' ? 'Pievienot sludinājumu' : language === 'ru' ? 'Создать объявление' : 'Create listing'}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-col gap-14">
                <div id="region-sec-shop" className="scroll-mt-24">
                  <BusinessEntitySection kind="shop" items={data.region_shops} language={language} />
                </div>
                <div id="region-sec-business" className="scroll-mt-24">
                  <BusinessEntitySection kind="business" items={data.region_businesses} language={language} />
                </div>
                <div id="region-sec-service" className="scroll-mt-24">
                  <CategorySection catKey="service" items={listings.service} language={language} regionSlug={slug} regionLabel={data.name} />
                </div>
                <div id="region-sec-project" className="scroll-mt-24">
                  <CategorySection catKey="project" items={listings.project} language={language} regionSlug={slug} regionLabel={data.name} />
                </div>
              </div>
            )}
          </div>

          {/* ============ SIDEBAR ============ */}
          <aside className="lg:col-span-4 flex flex-col gap-6">
            {/* Community panel — the SINGLE home for the registered-user count */}
            <CommunityPanel
              name={data.name}
              community={community}
              following={following}
              followerCount={followerCount}
              followBusy={followBusy}
              onFollow={handleFollow}
              language={language}
            />

            <LocalEcosystemMap
              center={data.map_center}
              sellers={data.local_sellers || []}
              language={language}
            />

            {/* Nearby places */}
            {data.nearby_locations?.length > 0 && (
              <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="region-nearby">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-[#4A6B53]" />
                  <h3 className="font-outfit font-bold text-[#121212]">
                    {language === 'lv' ? 'Tuvākās vietas' : language === 'ru' ? 'Ближайшие места' : 'Nearby places'}
                  </h3>
                </div>
                <ul className="space-y-1">
                  {data.nearby_locations.slice(0, 8).map((n) => (
                    <li key={n.slug}>
                      <Link
                        to={`/region/${n.slug}`}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FAFAFA] group"
                        data-testid={`nearby-${n.slug}`}
                      >
                        <span className="text-sm text-[#121212] group-hover:text-[#D97736]">{n.name}</span>
                        <span className="text-xs text-[#5C5C5C]">
                          {n.type === 'city' ? (language === 'lv' ? 'Pilsēta' : language === 'ru' ? 'Город' : 'City')
                            : n.type === 'district' ? (language === 'lv' ? 'Mikrorajons' : language === 'ru' ? 'Район' : 'District')
                            : (language === 'lv' ? 'Ciems' : language === 'ru' ? 'Село' : 'Village')}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Seasonal catalogs */}
            <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-[#D97736]" />
                <h3 className="font-outfit font-bold text-[#121212]">
                  {language === 'lv' ? 'Sezonas katalogi' : language === 'ru' ? 'Сезонные каталоги' : 'Seasonal catalogs'}
                </h3>
              </div>
              {catalogs.length === 0 ? (
                <p className="text-sm text-[#5C5C5C]">
                  {language === 'lv'
                    ? `Šobrīd nav aktīvu katalogu ${data.name} reģionā.`
                    : language === 'ru'
                    ? `Сейчас нет активных каталогов в ${data.name}.`
                    : `No active catalogs in ${data.name} right now.`}
                </p>
              ) : (
                <ul className="space-y-2">
                  {catalogs.slice(0, 8).map((c) => (
                    <li key={c.id || c.slug}>
                      <Link
                        to={`/catalog/${c.slug || c.id}?region=${slug}`}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FAFAFA] group"
                        data-testid={`region-catalog-${c.slug || c.id}`}
                      >
                        <span className="text-sm text-[#121212] group-hover:text-[#D97736] truncate">{c.name || c.title}</span>
                        <ChevronRight className="w-4 h-4 text-[#5C5C5C] group-hover:text-[#D97736] shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/catalog" className="mt-4 text-xs text-[#D97736] hover:underline flex items-center gap-1">
                {language === 'lv' ? 'Visi katalogi' : language === 'ru' ? 'Все каталоги' : 'All catalogs'}
                <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Own a shop CTA */}
            <div className="rounded-2xl p-6 text-white" style={{ background: '#4A6B53' }}>
              <div className="text-xs uppercase tracking-wider text-white/70 mb-2">
                {language === 'lv' ? 'Tev pieder veikals?' : language === 'ru' ? 'У вас есть магазин?' : 'Own a shop?'}
              </div>
              <h4 className="font-outfit font-bold text-lg mb-3">
                {language === 'lv' ? `Pārstāvi ${data.name}` : language === 'ru' ? `Представляй ${data.name}` : `Represent ${data.name}`}
              </h4>
              <p className="text-sm text-white/80 mb-4">
                {language === 'lv'
                  ? 'Pievieno savu veikalu vai pakalpojumu šajā vietā un sasniedz vietējos klientus.'
                  : language === 'ru'
                  ? 'Добавьте свой магазин или услугу здесь и привлекайте местных клиентов.'
                  : 'Add your shop or service here and reach local customers.'}
              </p>
              <Link to="/create-listing">
                <Button className="bg-white text-[#121212] hover:bg-white/90 w-full font-semibold">
                  {language === 'lv' ? 'Sākt' : language === 'ru' ? 'Начать' : 'Get started'}
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
